import { AdminApiError, type AdminEnvelopeOk, get } from './_core.js';

export interface ErrorGroup {
  groupId: string;
  errorKind: string | null;
  provider: string | null;
  model: string | null;
  kind: string;
  count: number;
  lastSeen: string;
  sampleSpanIds: string[];
}

export interface ErrorsListResponse {
  groups: ErrorGroup[];
  from: string;
  to: string;
}

export async function fetchErrors(opts: {
  errorKind?: string;
  from?: string;
  to?: string;
} = {}): Promise<ErrorsListResponse> {
  const params = new URLSearchParams();
  if (opts.errorKind) params.set('error_kind', opts.errorKind);
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  const qs = params.toString();
  const res = await get<ErrorsListResponse>(`/admin/errors${qs ? `?${qs}` : ''}`);
  return res.data;
}

// ─── Guardrails — turnos que el provider arruinó y el backend recuperó ────
//
// NO son errores (`is_error=false`: el usuario vio una respuesta normal), por
// eso viven en un endpoint aparte del inbox de errores. Se listan igual porque
// la pregunta que responden es "¿cuánto me está fallando el modelo?", y sin
// pantalla el guard taparía esa falla en vez de exponerla.

export interface GuardrailSample {
  spanId: string;
  sessionId: string | null;
  startedAt: string;
  /** El texto que el modelo dijo y no se mostró. `null` si el módulo es sensible. */
  suppressed: string | null;
}

export interface GuardrailGroup {
  provider: string | null;
  model: string | null;
  /** `degenerate_opening` = arrancó con un artefacto; `empty` = no dijo nada. */
  reason: string | null;
  /** `recovered` = el reintento salió bien; `fallback` = falló dos veces. */
  outcome: string | null;
  count: number;
  lastSeen: string;
  samples: GuardrailSample[];
}

/** CoT que SÍ vino marcado y el cliente de Gemini descartó. */
export interface ThoughtDropGroup {
  model: string | null;
  calls: number;
  parts: number;
  chars: number;
  lastSeen: string;
}

export interface GuardrailsResponse {
  from: string;
  to: string;
  /** Denominador: llamadas de reply en la ventana. Sin esto el count no se lee. */
  replyCalls: number;
  replyGuard: GuardrailGroup[];
  thoughtDrops: ThoughtDropGroup[];
}

export async function fetchGuardrails(
  opts: { from?: string; to?: string } = {},
): Promise<GuardrailsResponse> {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  const qs = params.toString();
  const res = await get<GuardrailsResponse>(`/admin/errors/guardrails${qs ? `?${qs}` : ''}`);
  return res.data;
}

// ─── Respuestas lentas — cuánto espera el usuario al coach ───────────────
//
// Tampoco son errores: la respuesta llegó y fue buena, sólo que tarde. Se mide
// el TTFT (el tiempo hasta la PRIMERA palabra, que es lo que el usuario vive
// como silencio) del span de reply, por (provider, modelo).

export interface SlowReplySample {
  spanId: string;
  sessionId: string | null;
  startedAt: string;
  ttftMs: number;
  durationMs: number;
}

export interface SlowReplyDay {
  /** `YYYY-MM-DD`, hora argentina. */
  day: string;
  calls: number;
  slow: number;
  maxTtftMs: number | null;
}

export interface SlowReplyGroup {
  provider: string | null;
  model: string | null;
  calls: number;
  /** Las que tienen TTFT medido: el denominador real de los percentiles. */
  measured: number;
  slow: number;
  verySlow: number;
  /** Contexto, no alarma: de los errores se ocupa el inbox de abajo. */
  errors: number;
  p50Ms: number | null;
  p95Ms: number | null;
  p99Ms: number | null;
  maxMs: number | null;
  worst: SlowReplySample | null;
  daily: SlowReplyDay[];
}

export interface SlowRepliesResponse {
  from: string;
  to: string;
  thresholds: { slowMs: number; verySlowMs: number };
  groups: SlowReplyGroup[];
}

export async function fetchSlowReplies(
  opts: { from?: string; to?: string } = {},
): Promise<SlowRepliesResponse> {
  const params = new URLSearchParams();
  if (opts.from) params.set('from', opts.from);
  if (opts.to) params.set('to', opts.to);
  const qs = params.toString();
  const res = await get<SlowRepliesResponse>(`/admin/errors/slow-replies${qs ? `?${qs}` : ''}`);
  return res.data;
}

export interface ErrorSpanDetail {
  id: string;
  traceId: string;
  sessionId: string | null;
  userId: string | null;
  kind: string;
  name: string;
  errorKind: string | null;
  /** null cuando la sesión es de un módulo sensible (`gated: true`). */
  errorPayload: unknown;
  /** Gate de privacidad: el payload se nulificó (módulo sensible). */
  gated: boolean;
  attributes: unknown;
  durationMs: number;
  startedAt: string;
  seen: { byAdminId: string; atIso: string } | null;
}

export async function fetchErrorDetail(spanId: string): Promise<ErrorSpanDetail> {
  const res = await get<ErrorSpanDetail>(`/admin/errors/${spanId}`);
  return res.data;
}

export async function markErrorSeen(spanId: string): Promise<void> {
  const res = await fetch(`/api/admin/errors/${spanId}/seen`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: '{}',
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { code: string; message: string } }
      | null;
    throw new AdminApiError(
      res.status,
      body?.error?.code ?? 'seen_failed',
      body?.error?.message ?? 'failed to mark seen',
    );
  }
}

// ─── BO-5 A.4 — Error group spark / snooze ───────────────────────────────

export async function fetchErrorGroupSpark(
  groupId: string,
): Promise<{ groupId: string; buckets: number[] }> {
  const res = await get<{ groupId: string; buckets: number[] }>(
    `/admin/errors/groups/${encodeURIComponent(groupId)}/spark`,
  );
  return res.data;
}

export async function snoozeErrorGroup(
  groupId: string,
  ttlMinutes: number,
  reason?: string,
): Promise<{ groupId: string; expiresAt: string }> {
  const res = await fetch(
    `/api/admin/errors/groups/${encodeURIComponent(groupId)}/snooze`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ttlMinutes, ...(reason ? { reason } : {}) }),
    },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { error?: { code: string; message: string } }
      | null;
    throw new AdminApiError(
      res.status,
      body?.error?.code ?? 'snooze_failed',
      body?.error?.message ?? 'failed to snooze',
    );
  }
  const json = (await res.json()) as AdminEnvelopeOk<{ groupId: string; expiresAt: string }>;
  return json.data;
}
